import { Formik } from "formik";
import { OctagonAlert } from "lucide-react";
import { useRef, useState } from "react";
import {
  Alert,
  Button,
  Card,
  Collapse,
  Form,
  Spinner,
  Stack,
} from "react-bootstrap";
import * as Yup from "yup";

import shipmentsService from "../../../../services/shipmentService";

const CompleteForm = ({ setSummary }) => {
  const [error, setError] = useState(null);

  const listingLabelInputRef = useRef(null);
  const fromRackIdInputRef = useRef(null);
  const toRackIdInputRef = useRef(null);

  const validationSchema = Yup.object().shape({
    listingLabel: Yup.string().required("Rack item label is required."),
    fromRackId: Yup.number()
      .typeError("FROM Rack ID must be a number.")
      .required("FROM Rack ID is required."),
    toRackId: Yup.number()
      .typeError("TO Rack ID must be a number.")
      .required("TO Rack ID is required."),
  });

  const initialValues = {
    listingLabel: "",
    fromRackId: "",
    toRackId: "",
  };

  return (
    <Card>
      <Card.Header className="border-bottom">
        <Card.Title>Transfer Rack Item</Card.Title>
        <Card.Subtitle>
          Scan the rack item's label (RWW, RWB, etc), the rack ID the item is
          coming from, and then the rack ID the item is going to.
        </Card.Subtitle>
      </Card.Header>
      <Card.Body>
        {error && (
          <Alert variant="danger" className="flex-fill alert-blink mb-3">
            <div className="alert-icon">
              <OctagonAlert
                className="position-relative top-50 start-50 translate-middle"
                size={20}
              />
            </div>
            <div className="alert-message font-weight-bold">
              {error.message}
            </div>
          </Alert>
        )}
        <Formik
          enableReinitialize
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={async (values, actions) => {
            setError(null);
            setSummary(null);
            console.log(values);

            const listingLabel = values.listingLabel.toUpperCase().trim();
            const fromRackId = Number(values.fromRackId);
            const toRackId = Number(values.toRackId);

            const result = await shipmentsService.transferRackItem({
              fnsku: listingLabel,
              fromRackId,
              toRackId,
            });

            if (result.success) {
              const updatedShipmentRackItem = result.updatedShipmentRackItem;

              setSummary({
                prevRackId: result.prevShipmentRackId,
                newRackId: updatedShipmentRackItem.shipmentRackId,
                rwwId: listingLabel,
                rackItemId: updatedShipmentRackItem.shipmentRackItemId,
                lastUpdated: new Date(
                  updatedShipmentRackItem.lastUpdated
                ).toLocaleString(),
              });

              setTimeout(() => {
                listingLabelInputRef.current?.focus();
              }, 0);

              actions.resetForm();
            } else {
              if (result.error) {
                setError({
                  message: "Something went wrong. Please try again.",
                });
                setTimeout(() => {
                  listingLabelInputRef.current?.focus();
                }, 0);

                return;
              } else {
                // If no item was found.
                if (result.shipmentRackItemNotFound) {
                  setError({
                    message: `No Rack Item found for ${listingLabel}.`,
                  });
                  setTimeout(() => {
                    listingLabelInputRef.current?.focus();
                  }, 0);

                  return;
                }

                // If no TO rack was found.
                if (result.toShipmentRackNotFound) {
                  setError({
                    message: `No Rack found for ${toRackId}.`,
                  });
                  setTimeout(() => {
                    toRackIdInputRef.current?.focus();
                  }, 0);

                  return;
                }

                // If multiple items were found, but none on the FROM rack were found.
                if (result.shipmentRackItemNotMatched) {
                  setError({
                    message: `No Rack Item found on Rack ${fromRackId}.`,
                  });
                  setTimeout(() => {
                    fromRackIdInputRef.current?.focus();
                  }, 0);

                  return;
                }
              }
            }
          }}
        >
          {({
            handleChange,
            handleBlur,
            setFieldTouched,
            handleSubmit,
            handleReset,
            touched,
            dirty,
            isSubmitting,
            isValid,
            errors,
            values,
          }) => (
            <Form onSubmit={handleSubmit}>
              <Form.Group className="mb-3">
                <Form.Label className="mb-0">
                  Rack Item Label<span className="text-danger ms-1">*</span>
                </Form.Label>
                <Form.Control
                  ref={listingLabelInputRef}
                  required
                  autoFocus
                  name="listingLabel"
                  size="lg"
                  placeholder="Scan or Enter Rack Item Label..."
                  value={values.listingLabel}
                  onBlur={handleBlur}
                  onChange={handleChange}
                  onFocus={(e) => {
                    e.target.select();
                  }}
                  onKeyUp={(e) => {
                    if (e.key === "Enter") {
                      if (Boolean(errors.listingLabel)) {
                        e.target.select();
                      } else {
                        fromRackIdInputRef.current?.focus();
                      }
                    }
                  }}
                  isInvalid={Boolean(touched.toRackId && errors.listingLabel)}
                  disabled={isSubmitting}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.listingLabel}
                </Form.Control.Feedback>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label className="mb-0">
                  FROM Rack ID<span className="text-danger ms-1">*</span>
                </Form.Label>
                <Form.Control
                  ref={fromRackIdInputRef}
                  required
                  name="fromRackId"
                  size="lg"
                  placeholder="Scan or Enter Rack ID..."
                  value={values.fromRackId}
                  onBlur={handleBlur}
                  onChange={handleChange}
                  onFocus={(e) => {
                    e.target.select();
                  }}
                  onKeyUp={(e) => {
                    if (e.key === "Enter") {
                      if (Boolean(errors.fromRackId)) {
                        e.target.select();
                      } else {
                        toRackIdInputRef.current?.focus();
                      }
                    }
                  }}
                  isInvalid={Boolean(touched.toRackId && errors.fromRackId)}
                  disabled={isSubmitting}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.fromRackId}
                </Form.Control.Feedback>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label className="m-0">
                  TO Rack ID
                  <span className="text-danger ms-1">*</span>
                </Form.Label>
                <Form.Control
                  ref={toRackIdInputRef}
                  required
                  name="toRackId"
                  size="lg"
                  placeholder="Scan or Enter Rack ID..."
                  value={values.toRackId}
                  onBlur={handleBlur}
                  onChange={handleChange}
                  onFocus={(e) => {
                    e.target.select();
                  }}
                  onKeyUp={(e) => {
                    if (e.key === "Enter") {
                      if (Boolean(errors.toRackId)) {
                        setFieldTouched("toRackId", true);
                        e.target.select();
                      } else {
                        handleSubmit();
                      }
                    }
                  }}
                  isInvalid={Boolean(touched.toRackId && errors.toRackId)}
                  disabled={isSubmitting}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.toRackId}
                </Form.Control.Feedback>
              </Form.Group>

              <Stack direction="horizontal">
                <Button
                  type="submit"
                  variant="outline-primary"
                  // disabled={isSubmitting || !dirty || !isValid}
                >
                  {isSubmitting ? (
                    <span>
                      <Spinner
                        className="me-2"
                        animation="border"
                        size="sm"
                        role="status"
                      />
                      Submitting...
                    </span>
                  ) : (
                    "Submit"
                  )}
                </Button>

                <Collapse in={dirty} dimension="width">
                  <div>
                    <Button
                      className="ms-3"
                      variant="outline-danger"
                      onClick={() => {
                        handleReset();
                        listingLabelInputRef.current?.focus();
                      }}
                      disabled={isSubmitting || !dirty}
                    >
                      Reset
                    </Button>
                  </div>
                </Collapse>
              </Stack>
            </Form>
          )}
        </Formik>
      </Card.Body>
    </Card>
  );
};

export default CompleteForm;
